import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch } from "react-redux";
import { ArrowLeft, BookOpen, Lock, Sparkles, X } from "lucide-react";
import SignIn from "../../pages/guest_pages/SignIn";
import SignUp from "../../pages/guest_pages/SignUp";
import ForgotPassword from "../../pages/guest_pages/ForgotPassword";
import { resetAuthFlowState } from "../../store/slices/userSlice";

/** Full-viewport overlay — above GuestNavbar (z-100), mega menu (z-110), scroll button (z-120). */
const AUTH_MODAL_Z = 250;

/**
 * Full auth (sign-in / sign-up / forgot) over content paywalls.
 * Portaled to document.body so it always stacks above the fixed navbar.
 */
export default function ArticleAuthFlowModal({
  open,
  onClose,
  onAuthenticated,
  openWith = "signup",
  articleTitle,
  sponsorName,
  returnPath,
  articleInterestCategory,
  articleInterestSubCategory,
}) {
  const dispatch = useDispatch();
  const [step, setStep] = useState(openWith === "login" ? "signin" : "signup");
  const [signInHandoff, setSignInHandoff] = useState({
    initialEmail: "",
    fromRegistration: false,
  });

  const isSignup = step === "signup";
  const isForgot = step === "forgot";
  const isSignin = step === "signin";

  const headerTitle =
    step === "signin"
      ? "Sign in"
      : step === "signup"
        ? "Create an account"
        : "Reset password";

  useEffect(() => {
    if (!open) return;
    setStep(openWith === "login" ? "signin" : "signup");
    setSignInHandoff({ initialEmail: "", fromRegistration: false });
  }, [open, openWith]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const handleClose = useCallback(() => {
    dispatch(resetAuthFlowState());
    onClose?.();
  }, [dispatch, onClose]);

  const goSignIn = useCallback((opts = {}) => {
    setSignInHandoff({
      initialEmail: opts.prefilledEmail || "",
      fromRegistration: Boolean(opts.fromRegistration),
    });
    setStep("signin");
  }, []);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <div
          className="fixed inset-0 flex items-center justify-center p-3 sm:p-5 md:p-6"
          style={{ zIndex: AUTH_MODAL_Z }}
          role="presentation"
        >
          <motion.button
            type="button"
            aria-label="Close"
            className="absolute inset-0 bg-slate-950/65 backdrop-blur-[10px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            onClick={handleClose}
          />

          <motion.div
            layout
            role="dialog"
            aria-modal="true"
            aria-labelledby="article-auth-title"
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{
              layout: { type: "spring", damping: 35, stiffness: 420, mass: 0.8 },
              opacity: { duration: 0.2 },
              y: { type: "spring", damping: 32, stiffness: 380, mass: 0.85 },
              scale: { type: "spring", damping: 32, stiffness: 380, mass: 0.85 },
            }}
            className={[
              "relative z-10 flex w-full flex-col overflow-hidden rounded-[1.35rem] border border-white/20 bg-white sm:rounded-[1.5rem]",
              "shadow-[0_32px_90px_-20px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.07)_inset]",
              isSignup
                ? "max-h-[min(92dvh,62rem)] min-h-[min(32rem,78dvh)]"
                : "max-h-[min(88dvh,54rem)]",
              isSignup
                ? "max-w-[min(100%,30rem)] sm:max-w-[min(98vw,48rem)] lg:max-w-[52rem]"
                : "max-w-[min(100%,24rem)] sm:max-w-[min(96vw,28rem)]",
            ].join(" ")}
          >
            <div
              className={[
                "relative shrink-0 overflow-hidden border-b border-white/10 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white",
                "before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:z-[1] before:h-px before:bg-gradient-to-r before:from-transparent before:via-[#FFD221]/45 before:to-transparent",
                isSignup
                  ? "px-4 pb-3 pt-3.5 sm:px-5 sm:pb-3 sm:pt-4 md:px-6"
                  : "px-4 pb-3 pt-3 sm:px-5 sm:pb-3.5 sm:pt-3.5",
              ].join(" ")}
            >
              <button
                type="button"
                onClick={handleClose}
                className="absolute right-2.5 top-2.5 z-[2] flex h-9 w-9 items-center justify-center rounded-full bg-white/[0.12] text-white backdrop-blur-sm transition hover:bg-white/22 sm:right-3 sm:top-3"
                aria-label="Close"
              >
                <X size={18} strokeWidth={2.25} />
              </button>

              <div
                className={[
                  "relative z-0 flex flex-col gap-2.5 sm:gap-3",
                  isSignup
                    ? "pr-11 sm:pr-12 md:flex-row md:items-center md:justify-between md:gap-6"
                    : "pr-11 sm:pr-[3.25rem]",
                ].join(" ")}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#FFD221]/35 bg-[#FFD221]/10 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.16em] text-[#FFD221] sm:text-[10px]">
                      <Lock size={10} strokeWidth={2.5} className="opacity-90" aria-hidden />
                      Member
                    </span>
                    <h2
                      id="article-auth-title"
                      className={[
                        "min-w-0 font-black leading-[1.15] tracking-tight",
                        isSignin || isForgot
                          ? "text-base sm:text-[1.125rem]"
                          : "text-lg sm:text-xl md:text-[1.35rem]",
                      ].join(" ")}
                    >
                      {headerTitle}
                    </h2>
                  </div>
                  {!isForgot ? (
                    <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/38 sm:text-xs sm:tracking-[0.14em]">
                      {isSignup ? "Unlock full article" : "Continue reading"}
                    </p>
                  ) : null}
                  {articleTitle ? (
                    <p className="mt-2 flex items-start gap-2 text-[13px] font-semibold leading-snug text-white/75 sm:text-sm">
                      <BookOpen
                        size={15}
                        className="mt-0.5 shrink-0 text-[#FFD221]"
                        strokeWidth={2}
                        aria-hidden
                      />
                      <span className={isSignup ? "line-clamp-2 lg:line-clamp-1" : "line-clamp-2"}>
                        {articleTitle}
                      </span>
                    </p>
                  ) : null}
                  {sponsorName ? (
                    <p className="mt-1.5 line-clamp-1 text-[10px] leading-tight text-white/40 sm:text-[11px]">
                      Routed to {sponsorName} when you connect
                    </p>
                  ) : null}
                </div>

                {!isForgot ? (
                  <div
                    className={[
                      "shrink-0",
                      isSignup ? "w-full md:w-auto md:min-w-[13.5rem] md:max-w-[18rem]" : "w-full",
                    ].join(" ")}
                  >
                    <div className="flex gap-0.5 rounded-2xl border border-white/14 bg-black/50 p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                      {[
                        { id: "signin", label: "Sign in", short: "Sign in" },
                        { id: "signup", label: "Create account", short: "Join" },
                      ].map(({ id, label, short }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => {
                            dispatch(resetAuthFlowState());
                            setStep(id);
                            if (id === "signin") {
                              setSignInHandoff({ initialEmail: "", fromRegistration: false });
                            }
                          }}
                          className={[
                            "flex min-h-[2.5rem] flex-1 items-center justify-center gap-1 rounded-[0.625rem] px-2 py-2 text-[11px] font-black uppercase tracking-wide transition sm:min-h-[2.625rem] sm:gap-1.5 sm:px-2.5 sm:text-xs",
                            step === id
                              ? "bg-[#FFD221] text-black shadow-md shadow-black/35"
                              : "text-white/48 hover:bg-white/[0.08] hover:text-white/92",
                          ].join(" ")}
                          aria-current={step === id ? "true" : undefined}
                        >
                          {id === "signup" ? (
                            <Sparkles size={13} className="hidden opacity-90 sm:inline" strokeWidth={2.25} />
                          ) : null}
                          <span className="max-[380px]:sr-only sm:not-sr-only">{label}</span>
                          <span className="hidden max-[380px]:inline sm:hidden">{short}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      dispatch(resetAuthFlowState());
                      setStep("signin");
                    }}
                    className="inline-flex min-h-[40px] shrink-0 items-center gap-2 self-start rounded-lg text-xs font-bold text-[#FFD221] transition hover:bg-white/[0.06] hover:text-white"
                  >
                    <ArrowLeft size={15} strokeWidth={2.25} />
                    Back to sign in
                  </button>
                )}
              </div>
            </div>

            <div className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain">
              <div
                className={[
                  "min-h-full bg-white",
                  isSignup ? "px-3 py-3 sm:px-6 sm:py-4 lg:px-8" : "px-5 py-6 sm:px-7 sm:py-7",
                ].join(" ")}
              >
                <div
                  className={
                    isSignup
                      ? "mx-auto w-full max-w-[44rem]"
                      : "mx-auto w-full max-w-[22rem] sm:max-w-[24rem]"
                  }
                >
                  <AnimatePresence mode="wait">
                    {step === "signin" ? (
                      <motion.div
                        key="signin"
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 12 }}
                        transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
                      >
                        <SignIn
                          embed
                          postAuthRedirect={returnPath}
                          onAuthenticated={onAuthenticated}
                          onRequestSignUp={() => {
                            dispatch(resetAuthFlowState());
                            setStep("signup");
                          }}
                          onRequestForgot={(emailFromField) => {
                            dispatch(resetAuthFlowState());
                            setSignInHandoff((prev) => ({
                              initialEmail: String(emailFromField || prev.initialEmail || "").trim(),
                              fromRegistration: prev.fromRegistration,
                            }));
                            setStep("forgot");
                          }}
                          initialEmail={signInHandoff.initialEmail}
                          fromRegistration={signInHandoff.fromRegistration}
                        />
                      </motion.div>
                    ) : null}

                    {step === "signup" ? (
                      <motion.div
                        key="signup"
                        initial={{ opacity: 0, x: 12 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -12 }}
                        transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
                        className="w-full"
                      >
                        <SignUp
                          key={`embed-signup-${articleInterestCategory || ""}-${articleInterestSubCategory || ""}-${returnPath || ""}`}
                          embed
                          postAuthRedirect={returnPath}
                          onEmbedClose={handleClose}
                          onRequestSignIn={(opts) => {
                            goSignIn(opts || {});
                          }}
                          embedArticleInterests={
                            articleInterestCategory && articleInterestSubCategory
                              ? {
                                  category: articleInterestCategory,
                                  subCategory: articleInterestSubCategory,
                                }
                              : undefined
                          }
                        />
                      </motion.div>
                    ) : null}

                    {step === "forgot" ? (
                      <motion.div
                        key="forgot"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
                      >
                        <ForgotPassword
                          embed
                          prefillEmail={signInHandoff.initialEmail}
                          onBackToSignIn={() => {
                            dispatch(resetAuthFlowState());
                            setStep("signin");
                          }}
                        />
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
